import type { Milestone, MilestoneDetail } from './types';

export const categories = [
  'Motor Skills',
  'Communication',
  'Social & Emotional',
  'Cognitive',
  'Boundary Stones',
  'Language',
  'Nutrition',
  'Self-Care',
  'Sleep',
  'Teeth',
] as const;

// Demo fixtures are authored in months; derive the raw week fields so the
// array still satisfies the Milestone type (only `categories` is used at
// runtime — real milestones come from rowToMilestone).
const FIXTURE_WEEKS_PER_MONTH = 4.345;
const fixtureWeeks = (m: number) => Math.round(m * FIXTURE_WEEKS_PER_MONTH);

export const milestones: Milestone[] = (
  [
  // Motor
  { id: 'm1', cat: 'Motor Skills', group: 'Motor – Gross', leap: 1, name: 'Lifts head during tummy time', desc: 'Holds head up briefly while on belly.', start: 0.5, end: 2, median: 1.5, status: 'done', achievedAt: 'wk 6' },
  { id: 'm2', cat: 'Motor Skills', group: 'Motor – Gross', leap: 4, name: 'Rolls front to back', desc: 'First full rotation, usually a surprise.', start: 2, end: 5, median: 4, status: 'done', achievedAt: 'wk 12' },
  { id: 'm3', cat: 'Motor Skills', group: 'Motor – Gross', leap: 5, name: 'Sits without support', desc: 'Holds upright for 30 seconds or more.', start: 4, end: 7, median: 5.5, status: 'now' },
  { id: 'm4', cat: 'Motor Skills', group: 'Motor – Gross', leap: 6, name: 'Crawls on hands and knees', desc: 'Coordinated forward locomotion.', start: 7, end: 10, median: 8.5, status: 'soon' },
  { id: 'm5', cat: 'Motor Skills', group: 'Motor – Gross', leap: 7, name: 'Pulls to stand', desc: 'Uses furniture to come up to feet.', start: 8, end: 12, median: 10, status: 'soon' },

  // Communication
  { id: 'c1', cat: 'Communication', group: 'Communication', leap: 2, name: 'Coos and gurgles', desc: 'Vowel sounds, often during eye contact.', start: 1, end: 3, median: 2, status: 'done', achievedAt: 'wk 8' },
  { id: 'c2', cat: 'Communication', group: 'Communication', leap: 4, name: 'Laughs out loud', desc: 'Full belly laughter, not just smiling.', start: 3, end: 5, median: 4, status: 'done', achievedAt: 'wk 13' },
  { id: 'c3', cat: 'Communication', group: 'Communication', leap: 5, name: 'Babbles consonants', desc: 'Strings like "ba-ba", "da-da" appear.', start: 4, end: 7, median: 5.5, status: 'now' },
  { id: 'c4', cat: 'Communication', group: 'Communication', leap: 5, name: 'Responds to own name', desc: 'Turns reliably when called.', start: 5, end: 8, median: 6.5, status: 'soon' },

  // Social & Emotional
  { id: 's1', cat: 'Social & Emotional', group: 'Social', leap: 2, name: 'Social smile', desc: 'Smiles back, not just reflex.', start: 1, end: 3, median: 2, status: 'done', achievedAt: 'wk 7' },
  { id: 's2', cat: 'Social & Emotional', group: 'Social', leap: 3, name: 'Recognizes familiar faces', desc: 'Lights up for parents and primary carers.', start: 2, end: 4, median: 3, status: 'done', achievedAt: 'wk 11' },
  { id: 's3', cat: 'Social & Emotional', group: 'Social', leap: 6, name: 'Shows stranger awareness', desc: 'Mild wariness of unfamiliar faces.', start: 5, end: 9, median: 7, status: 'soon' },
  { id: 's4', cat: 'Social & Emotional', group: 'Social', leap: 6, name: 'Plays peek-a-boo', desc: 'Anticipates the reveal, laughs.', start: 7, end: 9, median: 8, status: 'soon' },

  // Cognitive
  { id: 'k1', cat: 'Cognitive', group: 'Cognitive', leap: 1, name: 'Tracks moving objects', desc: 'Follows with eyes across the midline.', start: 0, end: 2, median: 1, status: 'done', achievedAt: 'wk 4' },
  { id: 'k2', cat: 'Cognitive', group: 'Cognitive', leap: 4, name: 'Reaches for objects', desc: 'Coordinated arm motion towards a target.', start: 3, end: 5, median: 4, status: 'now' },
  { id: 'k3', cat: 'Cognitive', group: 'Cognitive', leap: 6, name: 'Object permanence', desc: 'Looks for a toy that has been hidden.', start: 6, end: 9, median: 7.5, status: 'soon' },
  ] as Omit<Milestone, 'startWeeks' | 'endWeeks' | 'medianWeeks'>[]
).map((m) => ({
  ...m,
  startWeeks: fixtureWeeks(m.start),
  endWeeks: fixtureWeeks(m.end),
  medianWeeks: fixtureWeeks(m.median),
}));

export const milestoneDetails: Record<string, MilestoneDetail> = {
  m1: {
    long:
      'The baby raises their head briefly while lying on their stomach, typically 45 degrees or more. This is the first step in developing the neck and upper-body strength needed for rolling, sitting, and eventually crawling. It also helps prevent flat head syndrome (positional plagiocephaly).',
    whatToDo: [
      'Place baby on a firm, flat surface on their tummy for 2–3 minutes immediately after a nappy change, 2–3 times per day.',
      'Get down to their eye level and make eye contact or use a high-contrast black-and-white card to motivate head lifting.',
      'Place a small rolled towel under their chest (not under the head) to reduce strain if they are struggling.',
      'Try tummy time on your own chest while you recline — skin-to-skin contact encourages the effort.',
    ],
    whatToAvoid: [
      'Never leave the baby unsupervised during tummy time — they cannot yet roll back independently.',
      'Avoid starting tummy time immediately after feeding; wait at least 20 minutes to reduce reflux discomfort.',
      'Do not skip tummy time because the baby cries — short, frequent sessions build tolerance gradually.',
    ],
    avgStartW: 2,
    medianW: 6,
    stdDevW: 2,
    earlyFlagW: 10,
    dependsOn: [],
    mediaHint: 'Tummy time photo or 45-second video',
    media: [
      { id: 'mm1', type: 'image', seed: 'tummy-1' },
      { id: 'mm2', type: 'video', seed: 'tummy-2', duration: '0:32' },
      { id: 'mm3', type: 'image', seed: 'tummy-3' },
      { id: 'mm4', type: 'image', seed: 'tummy-4' },
    ],
    history: [
      { id: 'h1', date: 'Today', note: '3 minutes after nappy change — held it for ~5 sec', kind: 'task' },
      { id: 'h2', date: 'Yesterday', note: 'Skin-to-skin on chest, 4 minutes', kind: 'task' },
      { id: 'h3', date: 'May 22', note: 'Black-and-white card; tracked it across midline', kind: 'task' },
      { id: 'h4', date: 'May 20', note: 'First chin lift above the mat', kind: 'note' },
      { id: 'h5', date: 'May 18 · wk 6', note: 'Achieved — sustained head lift, ~45°', kind: 'achieved' },
    ],
  },
  m3: {
    long:
      'The baby holds an unsupported sitting position for half a minute or more, eventually freeing their hands to play. Core control here unlocks crawling, reaching across the midline, and seated mealtimes.',
    whatToDo: [
      'Sit on the floor facing baby with your legs spread; let them sit between your shins for a safety net.',
      "Place a favourite toy just out of arm's reach to encourage forward-leaning core engagement.",
      'Use a firm cushion behind, not at the sides, so falls are soft but lateral muscles still recruit.',
      'Practice on a low-pile rug rather than the bed — feedback from a stable surface is easier to learn from.',
    ],
    whatToAvoid: [
      'Do not prop with cushions and walk away — assisted sitting without supervision is a fall risk.',
      'Avoid Bumbo-style seats for more than 10 minutes — they can mask weakness in trunk muscles.',
      "Don't force a longer session if baby slumps; better to do four 30-second tries than one tired five-minute one.",
    ],
    avgStartW: 16,
    medianW: 24,
    stdDevW: 4,
    earlyFlagW: 36,
    dependsOn: ['m1'],
    mediaHint: 'Seated play photo or short clip',
    history: [
      { id: 'h1', date: 'Today', note: 'Held for 12 seconds, reached for ring stack', kind: 'task' },
      { id: 'h2', date: 'Yesterday', note: 'Toy out of reach, 3 attempts', kind: 'task' },
      { id: 'h3', date: 'May 21', note: 'First unprompted sit-up from side', kind: 'note' },
    ],
  },
};
